/**
 * Trends: what customers are choosing, as data only — never a verdict.
 *
 * Input is each part's weekly use for the last N weeks and the same weeks a
 * year earlier (GET /api/trends). Output groups parts by an attribute (finish,
 * colour, size…) and reports, for each value: units in a recent window and the
 * same window last year, its share of the category then and now, and
 * momentum (the latest half of the window against the half before) — and
 * breadth: how many distinct orders and customers those units came from, so a
 * move made by many customers can be told from one big order.
 *
 * No statuses, thresholds or recommendations: the page and the AI sort by
 * size of change and leave the judgement to the reader.
 */
import { skuAttributes } from "./sku_attributes.ts";

/** One part's weekly use. Arrays run oldest → newest; index i in `now` and `last` are the same calendar week a year apart. */
export interface TrendSku {
  sku: string;
  item_type: string;
  now: { alone: number[]; inside: number[] };
  last: { alone: number[]; inside: number[] };
  /**
   * The orders that took this part, as indexes into TrendsResponse.orders,
   * each packed as `order * 4 + kind` — kind 1: some units sold on their own,
   * 2: some inside umbrellas (3: both). Absent from an API without breadth.
   */
  orders?: { now: number[]; last: number[] };
  /** Operations doesn't keep it in stock (api/src/data/not_stocked.ts). */
  not_stocked?: boolean;
}

/** Each order: [week index (same as the weekly arrays), customer number]. Customers are numbered alike across both years. */
export type TrendOrders = { now: Array<[number, number]>; last: Array<[number, number]> };

/** GET /api/trends response. */
export interface TrendsResponse {
  category: string;
  weeks: number;
  /** Last full day included (yesterday). */
  end: string;
  /** First day of the oldest week this year, and the same day last year. */
  from: string;
  last_from: string;
  skus: TrendSku[];
  /** The orders behind the units (breadth); see TrendSku.orders. */
  orders?: TrendOrders;
  generated_at: number;
}

/** "all" = every unit used; "umbrellas" = only units that went out inside finished umbrellas (no spares). */
export type TrendBasis = "all" | "umbrellas";

export interface TrendRow {
  value: string;
  skus: string[];
  /** Units in the recent window, and the same weeks last year. */
  units: number;
  units_last_year: number;
  /** % change vs last year; null when last year had none. */
  change_pct: number | null;
  /** Share of the category's units in the window, now and a year ago (0–1). */
  share: number;
  share_last_year: number;
  /** Percentage points. */
  share_change_pts: number;
  /** Latest half of the window vs the half before, %; null when the earlier half had none. */
  momentum_pct: number | null;
  /** Weekly units for every week returned, this year and last year. */
  weekly: number[];
  weekly_last_year: number[];
  /** Of `units`, how many went out inside finished umbrellas. */
  in_umbrellas: number;
  /**
   * Distinct orders (invoices / cash sales) and customers behind `units`, and
   * behind `units_last_year`. Counted over the whole group and window, so an
   * order taking several of the group's parts counts once. Intercompany
   * excluded. null when the API has no breadth data.
   */
  orders: number | null;
  customers: number | null;
  orders_last_year: number | null;
  customers_last_year: number | null;
}

export interface TrendTable {
  rows: TrendRow[];
  total: number;
  total_last_year: number;
  /** Breadth of the whole category (distinct, so not the sum of the rows). */
  orders: number | null;
  customers: number | null;
  orders_last_year: number | null;
  customers_last_year: number | null;
  window_weeks: number;
}

const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);
const pct = (a: number, b: number) => (b > 0 ? (a / b - 1) * 100 : null);

function weekly(s: TrendSku["now"], basis: TrendBasis): number[] {
  return s.inside.map((v, i) => (basis === "umbrellas" ? v : v + (s.alone[i] ?? 0)));
}

/**
 * Distinct orders and customers among `packed` (TrendSku.orders entries) that
 * fall in weeks >= `fromWeek` and match the basis. Sets, not sums: the same
 * order reached through two parts, or the same customer on two orders, counts
 * once.
 */
function breadth(
  packed: number[],
  table: Array<[number, number]>,
  fromWeek: number,
  basis: TrendBasis,
): { orders: number; customers: number } {
  const orders = new Set<number>();
  const customers = new Set<number>();
  for (const p of packed) {
    if (basis === "umbrellas" && !(p & 2)) continue;
    const o = p >> 2;
    const row = table[o];
    if (!row || row[0] < fromWeek) continue;
    orders.add(o);
    customers.add(row[1]);
  }
  return { orders: orders.size, customers: customers.size };
}

/**
 * Group parts by one attribute. `windowWeeks` is the recent window (the last
 * N weeks of the series); momentum compares its later half with its earlier
 * half. Parts the code says nothing about for this dimension are grouped as
 * "Not in code".
 */
export function trendTable(
  skus: TrendSku[],
  dimension: string,
  windowWeeks: number,
  basis: TrendBasis = "all",
  /** TrendsResponse.orders; without it the breadth fields are null. */
  orders?: TrendOrders,
): TrendTable {
  const groups = new Map<
    string,
    { skus: string[]; now: number[]; last: number[]; inside: number; oNow: number[]; oLast: number[] }
  >();
  let n = 0;
  for (const s of skus) {
    // "sku" lists parts one by one (a value's drill-down).
    const value = dimension === "sku" ? s.sku : skuAttributes(s.sku, s.item_type)[dimension] ?? "Not in code";
    const now = weekly(s.now, basis);
    const last = weekly(s.last, basis);
    n = Math.max(n, now.length);
    const g = groups.get(value) ?? { skus: [], now: [], last: [], inside: 0, oNow: [], oLast: [] };
    g.skus.push(s.sku);
    if (s.orders) {
      g.oNow.push(...s.orders.now);
      g.oLast.push(...s.orders.last);
    }
    now.forEach((v, i) => (g.now[i] = (g.now[i] ?? 0) + v));
    last.forEach((v, i) => (g.last[i] = (g.last[i] ?? 0) + v));
    g.inside += sum(s.now.inside.slice(-windowWeeks));
    groups.set(value, g);
  }
  const w = Math.min(windowWeeks, n);
  const half = Math.max(1, Math.floor(w / 2));
  const fill = (xs: number[]) => Array.from({ length: n }, (_, i) => xs[i] ?? 0);
  const all = [...groups.entries()].map(([value, g]) => {
    const now = fill(g.now);
    const last = fill(g.last);
    return { value, g, now, last, units: sum(now.slice(-w)), unitsLy: sum(last.slice(-w)) };
  });
  const total = sum(all.map((x) => x.units));
  const totalLy = sum(all.map((x) => x.unitsLy));
  // The window is the last w of n weeks; both years share the week index.
  const count = (nowPacked: number[], lastPacked: number[]) => {
    if (!orders) return { orders: null, customers: null, orders_last_year: null, customers_last_year: null };
    const a = breadth(nowPacked, orders.now, n - w, basis);
    const b = breadth(lastPacked, orders.last, n - w, basis);
    return { orders: a.orders, customers: a.customers, orders_last_year: b.orders, customers_last_year: b.customers };
  };
  const rows: TrendRow[] = all.map(({ value, g, now, last, units, unitsLy }) => {
    const share = total > 0 ? units / total : 0;
    const shareLy = totalLy > 0 ? unitsLy / totalLy : 0;
    const recent = sum(now.slice(-half));
    const before = sum(now.slice(-2 * half, -half));
    return {
      value,
      skus: g.skus,
      units,
      units_last_year: unitsLy,
      change_pct: pct(units, unitsLy),
      share,
      share_last_year: shareLy,
      share_change_pts: (share - shareLy) * 100,
      momentum_pct: pct(recent, before),
      weekly: now,
      weekly_last_year: last,
      in_umbrellas: g.inside,
      ...count(g.oNow, g.oLast),
    };
  });
  // Biggest movement in share first, either direction.
  rows.sort((a, b) => Math.abs(b.share_change_pts) - Math.abs(a.share_change_pts) || b.units - a.units);
  const totalBreadth = count(
    all.flatMap((x) => x.g.oNow),
    all.flatMap((x) => x.g.oLast),
  );
  return { rows, total, total_last_year: totalLy, ...totalBreadth, window_weeks: w };
}
